<template>
  <v-dialog v-model="visibility" persistent max-width="1156px">
    <v-card v-if="loading" flat height="300px" class="rounded-xl">
      <v-row no-gutters style="height: 50vh" align="center" justify="center">
        <v-progress-circular :size="50" color="#1b65f2" indeterminate></v-progress-circular>
      </v-row>
    </v-card>
    <v-card v-else class="rounded-xl py-2 px-3">
      <div style="height: 100%">
        <v-card-title class="pa-3">
          <h4 class="text-capitalize dialog-title pr-1">{{ t('$vuetify.__General.motion_dialog.virtual_avatar_motion') }}</h4>
        </v-card-title>
        <v-row no-gutters class="py-2" style="max-height: calc(90vh - 150px); overflow-y: auto">
          <v-col cols="6" class="px-3">
            <v-card class="pa-6" height="100%" style="overflow-y: auto; border-radius: 1rem">
              <v-row no-gutters class="card-title">{{ t('$vuetify.__General.motion_dialog.animation_preview') }}</v-row>
              <v-row no-gutters class="grey-small-title pt-1">{{ t('$vuetify.__General.motion_dialog.hint') }}</v-row>
              <v-row no-gutters class="py-8">
                <!-- Action & Gesture -->
                <v-col cols="6" class="pr-6">
                  <v-row no-gutters align="center">
                    <v-icon size="24" color="#000000" class="pr-2">mdi-run-fast</v-icon>
                    <span class="dialog-subtitle">{{ t('$vuetify.__General.motion_dialog.action_gesture') }}</span>
                  </v-row>
                  <v-card flat class="animation-card mt-2" height="250px">
                    <v-list height="248" class="py-2" style="overflow-y: auto">
                      <v-list-item
                        v-for="(item, i) in model_info.tap_body_motions"
                        :key="i"
                        @click="selectMotion(item, 'TapBody')"
                        :class="selected_animation === item ? 'selected-animation' : ''"
                        style="height: 48px"
                      >
                        <span>{{ getFileName(item.url) }}</span>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>

                <!-- Idle Motion -->
                <v-col cols="6" class="pr-6">
                  <v-row no-gutters align="center">
                    <v-icon size="24" color="#000000" class="pr-2">mdi-human-male</v-icon>
                    <span class="dialog-subtitle">{{ t('$vuetify.__General.motion_dialog.idle_motion') }}</span>
                  </v-row>
                  <v-card flat class="animation-card mt-2" height="250px">
                    <v-list height="232" class="py-2" style="overflow-y: auto">
                      <v-list-item
                        :class="selected_animation === item ? 'selected-animation' : ''"
                        style="height: 48px"
                        v-for="(item, i) in model_info.idle_motions"
                        :key="i"
                        @click="selectMotion(item, 'Idle')"
                      >
                        <span>{{ getFileName(item.url) }}</span>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>

              <!-- lip sync -->
              <v-row no-gutters class="lip-sync-switch px-5 py-2" align="center">
                <v-switch color="#1b65f2" v-model="is_lip_sync_animation" hide-details>
                  <template v-slot:label>
                    <span class="black-small-title pl-1">{{ t('$vuetify.__General.motion_dialog.lip_sync') }}</span>
                  </template>
                </v-switch>
              </v-row>
              <div v-if="is_lip_sync_animation">
                <v-row no-gutters class="grey-small-text pt-4">{{ t('$vuetify.__General.motion_dialog.lip_sync_hint') }}</v-row>
                <v-row no-gutters>
                  <v-radio-group v-model="lip_sync_sentence" hide-details>
                    <v-radio v-for="(sentence, i) in model_info['TTS']" :key="i" :value="sentence" @update:modelValue="playSound(sentence.url)">
                      <template v-slot:label>
                        <span class="dialog-subtitle">{{ sentence.text }}</span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-row>
              </div>
            </v-card>
          </v-col>

          <v-col cols="6" class="px-3">
            <v-card flat height="41rem">
              <!-- Selected animation -->
              <v-row no-gutters class="grey-small-title" style="height: 3rem">
                <span v-if="selected_animation">{{ t('$vuetify.__General.motion_dialog.animation') }} {{ getFileName(selected_animation.url) }}</span>
                <v-spacer />
                <v-tooltip location="top" content-class="tooltip-color" open-delay="200" max-width="208px">
                  <template v-slot:activator="{ props: tooltip_props }">
                    <v-btn variant="outlined" rounded height="30" width="50" class="rounded-btn mr-3" v-bind="tooltip_props" @click="reload()">
                      <v-icon size="18">mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ t('$vuetify.__General.refresh') }}</span>
                </v-tooltip>
              </v-row>

              <!-- Iframe -->
              <v-row no-gutters class="grey-small-desc" style="width: 100%; height: calc(100% - 6rem)">
                <iframe v-if="iframe_visibility" ref="avatar_iframe" :src="src" height="100%" width="100%" frameBorder="0"></iframe>
              </v-row>

              <!-- Hint -->
              <v-row no-gutters class="grey-small-desc" align="center" justify="center" style="height: 3rem">
                {{ t('$vuetify.__General.motion_dialog.animation_hint') }}
              </v-row>
            </v-card>
          </v-col>
        </v-row>
        <v-card-actions class="px-3 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="flat" color="#1b65f2" rounded class="text-capitalize px-3" @click="store.commit('RESET_BROWSE_MOTION_DIALOG')">
            {{ t('$vuetify.close') }}
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useLocale } from 'vuetify'
const { t } = useLocale()
import { useStore } from 'vuex'
const store = useStore()

import { computed, ref, watch } from 'vue'

const avatar_iframe = ref()
const iframe_visibility = ref(true)
const avatar_url = ref(process.env.VUE_APP_AVATAR_ENDPOINT)
const access_token = ref(process.env.VUE_APP_AVATAR_ACCESS_TOKEN)

const selected_animation = ref(null)
const is_lip_sync_animation = ref(false)
const lip_sync_sentence = ref(null)
const visibility = computed(() => store.state.browseMotionDialog.visibility)
const loading = computed(() => store.state.browseMotionDialog.loading)
const model_info = computed(() => store.state.browseMotionDialog.model_info)
const src = computed(() => `${avatar_url.value}?model_id=${model_info.value.model_id}&access_token=${access_token.value}&scale=1`)
const getFileName = (url) => {
  return url.split('/')[4]
}
const reload = () => {
  iframe_visibility.value = false
  setTimeout(() => {
    iframe_visibility.value = true
  }, 500)
}
const selectMotion = (item, type) => {
  selected_animation.value = item
  avatar_iframe.value.contentWindow.postMessage({ type: 'playSound', url: lip_sync_sentence.value?.url, motion: { group: type, motion_id: item.id } }, '*')
}
const playSound = (url) => {
  avatar_iframe.value.contentWindow.postMessage({ type: 'playSound', url: url }, '*')
}
watch(
  model_info,
  () => {
    reload()
  },
  { deep: true }
)
watch(is_lip_sync_animation, () => {
  if (!is_lip_sync_animation.value) lip_sync_sentence.value = null
})
</script>

<style scoped>
.animation-card {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  overflow-y: auto;
}

.lip-sync-switch {
  background: #f6f5f5;
  border-radius: 12px;
  width: 100%;
}

.selected-animation {
  background: rgba(27, 101, 242, 0.05);
}
</style>
